import { DATA_BASE_URL } from "./env";
import { pad } from "./format";
import type { DayData } from "@/types/lex";

/**
 * In-memory cache of fetched days, keyed by day number. The data is
 * immutable for a given day, so a single fetch per session is enough.
 * Browser HTTP cache + R2 cache headers handle cross-session caching.
 */
const memCache = new Map<number, Promise<DayData>>();

/**
 * Build the canonical URL for a day's JSON payload.
 */
export function dayUrl(day: number): string {
  return `${DATA_BASE_URL}/day-${pad(day)}.json`;
}

/**
 * Fetch one day's payload. De-dupes in-flight requests for the same day.
 *
 * Throws when the response is not OK so the caller (a hook) can surface the
 * error. No automatic retry — let the UI offer a manual retry button.
 */
export function fetchDay(day: number): Promise<DayData> {
  const existing = memCache.get(day);
  if (existing) return existing;

  const promise = (async () => {
    const res = await fetch(dayUrl(day), { credentials: "omit" });
    if (!res.ok) {
      throw new Error(`Failed to load day ${day}: HTTP ${res.status}`);
    }
    const data = (await res.json()) as DayData;
    if (!data || typeof data.day !== "number" || !Array.isArray(data.words)) {
      throw new Error(`Day ${day} payload is malformed`);
    }
    return data;
  })();

  // Drop the cache entry on failure so the next call retries fresh.
  promise.catch(() => memCache.delete(day));
  memCache.set(day, promise);
  return promise;
}

/**
 * Drop the in-memory cache. Surface this from a Settings → "Clear cache"
 * button. Does not clear the browser HTTP cache.
 */
export function clearDayCache(): void {
  memCache.clear();
}
